import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Icon from 'components/Icon/Icon';
import Image from 'components/Image/Image';
import { loadPatientAvatarQuery } from 'context/appContext';
import { formatPatientAgeAndGender, formatPatientDisplayName, formatPatientDOB } from 'utils/utils';
import { Patient } from 'types/types';

type PatientProfileHeaderProps = {
  patient: Patient;
  onBack: () => void;
};

function PatientProfileHeader({ patient, onBack }: PatientProfileHeaderProps) {
  // Signs the avatar URL via the shared react-query cache, so the result is
  // reused across patient list cards and any header instance.
  const { data: syncedAvatarPhoto } = useQuery({
    ...loadPatientAvatarQuery(patient.profilePicture),
    enabled: !!patient.profilePicture && !patient.signedAvatar,
  });

  const dateOfBirth = formatPatientDOB(patient);

  return (
    <div className="flex items-center tablet:space-x-8 tablet:px-10">
      <button
        onClick={onBack}
        type="button"
        aria-label="Go back"
        className="hidden tablet:flex flex-row items-center"
      >
        <Icon id="goBack" className="w-8 h-8" />
      </button>
      <div className="flex items-center space-x-5 shrink-0">
        <Image
          className="h-[70px] w-[70px] tablet:h-[80px] tablet:w-[80px] rounded-full shrink-0"
          src={patient.signedAvatar || syncedAvatarPhoto || 'image_patient_avatar_placeholder.png'}
          alt={`${patient.firstName} ${patient.lastName}`}
          bgColor={patient.avatarPlaceholderColor}
        />
        <div>
          <h1 className="text-river-black font-medium text-[20px]">
            {formatPatientDisplayName(patient)}
          </h1>
          <p className="text-sm mt-1">{formatPatientAgeAndGender(patient)}</p>
          {dateOfBirth ? (
            <span className="inline-flex items-center mt-1 rounded-full bg-river-blue/10 px-2.5 py-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-river-blue mr-1.5">
                DOB
              </span>
              <span className="text-[13px] text-river-black">{dateOfBirth}</span>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PatientProfileHeader;
